import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { toast } from "react-toastify";
import {
  Book,
  Clock,
  Check,
  ArrowLeft,
  Additem,
  Archive,
  Star,
  StarOutline,
  DocumentText,
  Award,
} from "iconsax-reactjs";
import {
  fetchAssignmentTemplate,
  submitAssignment,
  clearSubmissionResult,
} from "../../../redux/slices/progressSlice";
import { useFetchUser } from "../../../utils/useUserStore";

function AssignmentDetail() {
  const { templateId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useFetchUser();

  const {
    assignmentTemplate,
    templateLoading,
    templateError,
    submitting,
    submissionResult,
    submitError,
  } = useSelector((state) => state.progress);

  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitForm, setSubmitForm] = useState({
    file: null,
    description: "",
  });

  useEffect(() => {
    if (templateId) {
      dispatch(fetchAssignmentTemplate(templateId));
    }
  }, [dispatch, templateId]);

  // Handle successful submission
  useEffect(() => {
    if (submissionResult) {
      toast.success(submissionResult.message || "Assignment submitted successfully!");
      setShowSubmitModal(false);
      setSubmitForm({ file: null, description: "" });
      dispatch(clearSubmissionResult());
      // Refresh the template details
      dispatch(fetchAssignmentTemplate(templateId));
    }
  }, [submissionResult, dispatch, templateId]);

  // Handle submission error
  useEffect(() => {
    if (submitError) {
      // Handle specific error cases
      if (submitError.status === 400) {
        toast.error("You have already submitted this assignment. Please wait for grading.");
      } else if (submitError.status === 403) {
        toast.error("You are not enrolled in this course.");
      } else if (submitError.status === 404) {
        toast.error("Assignment not found or not yet released.");
      } else {
        toast.error(submitError.message || "Failed to submit assignment");
      }
    }
  }, [submitError]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Calculate score percentage
  const getScorePercentage = (score, maxScore) => {
    if (!score || !maxScore) return 0;
    return Math.round((score / maxScore) * 100);
  };

  // Get grade color
  const getGradeColor = (percentage) => {
    if (percentage >= 70) return "text-success";
    if (percentage >= 50) return "text-warning";
    return "text-danger";
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File size must be less than 10MB");
        return;
      }
      setSubmitForm({ ...submitForm, file });
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = assignmentTemplate;
    await dispatch(
      submitAssignment({
        templateId: templateId,
        cohortId: data?.template?.cohort_id || data?.cohort?.id,
        courseId: data?.course?.id,
        weekNumber: data?.template?.week_number,
        file: submitForm.file,
        description: submitForm.description,
      })
    );
  };

  if (templateLoading) {
    return (
      <div className="dashboard-main-section">
        <div className="wrapper min-vh-100">
          <div className="header-area mb-4">
            <Skeleton width={100} height={20} />
            <Skeleton width={300} height={40} className="mt-2" />
          </div>
          <div className="card">
            <div className="card-body">
              <Skeleton height={200} />
              <Skeleton count={5} className="mt-3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (templateError) {
    return (
      <div className="dashboard-main-section">
        <div className="wrapper min-vh-100">
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">Error</h4>
            <p>
              {templateError.message || "Failed to load assignment details. Please try again."}
            </p>
            <div className="d-flex gap-2">
              <button
                className="btn btn-danger"
                onClick={() => dispatch(fetchAssignmentTemplate(templateId))}
              >
                Retry
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => navigate(-1)}
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { template, course, user_submission, can_submit, is_completed } = assignmentTemplate || {};

  // Determine what to show based on status
  const showSubmitForm = can_submit && !user_submission;
  const showGradedResult = is_completed && user_submission?.status === "graded";
  const showAwaitingGrading = user_submission?.status === "submitted";
  const showReturned = user_submission?.status === "returned";

  return (
    <div className="dashboard-main-section">
      <div className="wrapper min-vh-100">
        {/* Header */}
        <div className="header-area mb-4">
          <button
            className="btn btn-link text-decoration-none mb-2 p-0"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={20} className="me-1" />
            Back
          </button>
          <div className="d-flex align-items-center gap-3">
            <div>
              <span className="badge bg-secondary mb-2">
                Week {template?.week_number}
              </span>
              <h1 className="h4 fw-semibold mb-0">{template?.title || "Assignment Details"}</h1>
            </div>
          </div>
          <p className="text-muted">{course?.title}</p>
        </div>

        <div className="row">
          {/* Main Content - Assignment Details */}
          <div className="col-lg-8">
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="h6 fw-semibold mb-0">
                  <DocumentText size={18} className="me-2" />
                  Assignment Description
                </h5>
              </div>
              <div className="card-body">
                <p style={{ whiteSpace: "pre-wrap" }}>
                  {template?.description || "No description provided."}
                </p>
                
                <hr />
                
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <span className="text-muted small d-block">Maximum Score</span>
                      <span className="fw-semibold">{template?.max_score} points</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <span className="text-muted small d-block">Released Date</span>
                      <span className="fw-semibold">{formatDate(template?.released_at)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Section based on status */}
            {showSubmitForm && (
              <div className="card">
                <div className="card-header bg-primary text-white">
                  <h5 className="h6 fw-semibold mb-0">
                    <Additem size={18} className="me-2" />
                    Submit Your Work
                  </h5>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <label className="form-label">Upload File (Optional)</label>
                      <input
                        type="file"
                        className="form-control"
                        onChange={handleFileChange}
                        accept=".pdf,.doc,.docx,.zip,.rar"
                      />
                      <small className="text-muted">
                        Accepted formats: PDF, DOC, DOCX, RAR (Max 10MB)
                      </small>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Description (Optional)</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={submitForm.description}
                        onChange={(e) =>
                          setSubmitForm({ ...submitForm, description: e.target.value })
                        }
                        placeholder="Add any notes or explanation about your submission..."
                      />
                    </div>

                    {submitForm.file && (
                      <div className="alert alert-info">
                        <Archive size={16} className="me-2" />
                        <strong>Selected:</strong> {submitForm.file.name} (
                        {(submitForm.file.size / 1024).toFixed(2)} KB)
                      </div>
                    )}

                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Additem size={18} className="me-2" />
                          Submit Assignment
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            )}

            {/* Awaiting Grading Status */}
            {showAwaitingGrading && (
              <div className="card border-info">
                <div className="card-header bg-info text-white">
                  <h5 className="h6 fw-semibold mb-0">
                    <Clock size={18} className="me-2" />
                    Awaiting Grading
                  </h5>
                </div>
                <div className="card-body">
                  <div className="alert alert-info mb-0">
                    <p className="mb-2">
                      <strong>Status:</strong> Submitted
                    </p>
                    <p className="mb-0">
                      Your assignment has been submitted and is awaiting evaluation by the instructor.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Graded Result */}
            {showGradedResult && (
              <div className="card border-success">
                <div className="card-header bg-success text-white">
                  <h5 className="h6 fw-semibold mb-0">
                    <Award size={18} className="me-2" />
                    Graded Result
                  </h5>
                </div>
                <div className="card-body">
                  <div className="text-center mb-4">
                    <div
                      className={`display-4 fw-bold ${getGradeColor(
                        getScorePercentage(user_submission.score, user_submission.max_score)
                      )}`}
                    >
                      {user_submission.score}
                      <span className="text-muted fs-5">/{user_submission.max_score}</span>
                    </div>
                    <div className="text-muted">
                      {getScorePercentage(user_submission.score, user_submission.max_score)}%
                    </div>
                    
                    {/* Star rating based on score */}
                    <div className="mt-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          size={24}
                          variant={star <= Math.ceil(getScorePercentage(user_submission.score, user_submission.max_score) / 20) ? "Filled" : "Outline"}
                          className={star <= Math.ceil(getScorePercentage(user_submission.score, user_submission.max_score) / 20) ? "text-warning" : "text-muted"}
                        />
                      ))}
                    </div>
                  </div>

                  {user_submission.remarks && (
                    <div className="alert alert-light">
                      <h6 className="fw-semibold">Instructor Feedback:</h6>
                      <p className="mb-0">{user_submission.remarks}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Returned for Revision */}
            {showReturned && (
              <div className="card border-warning">
                <div className="card-header bg-warning text-dark">
                  <h5 className="h6 fw-semibold mb-0">
                    <DocumentText size={18} className="me-2" />
                    Returned for Revision
                  </h5>
                </div>
                <div className="card-body">
                  <div className="alert alert-warning mb-0">
                    <p className="mb-2">
                      <strong>Status:</strong> Returned
                    </p>
                    {user_submission.remarks && (
                      <>
                        <p className="mb-0"><strong>Feedback:</strong> {user_submission.remarks}</p>
                      </>
                    )}
                  </div>
                  {can_submit && (
                    <button
                      className="btn btn-primary mt-3"
                      onClick={() => setShowSubmitModal(true)}
                    >
                      <Additem size={18} className="me-2" />
                      Resubmit
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Status & Info */}
          <div className="col-lg-4">
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="h6 fw-semibold mb-0">Assignment Status</h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <span className="text-muted small d-block">Current Status</span>
                  {is_completed ? (
                    <span className="badge bg-success">Completed</span>
                  ) : user_submission?.status === "submitted" ? (
                    <span className="badge bg-info">Submitted</span>
                  ) : user_submission?.status === "returned" ? (
                    <span className="badge bg-warning">Needs Revision</span>
                  ) : (
                    <span className="badge bg-secondary">Not Started</span>
                  )}
                </div>
                
                {user_submission && (
                  <>
                    <div className="mb-3">
                      <span className="text-muted small d-block">Submitted On</span>
                      <span className="fw-semibold">
                        {formatDate(user_submission.submitted_at)}
                      </span>
                    </div>
                    
                    {user_submission.score !== null && (
                      <div className="mb-3">
                        <span className="text-muted small d-block">Score</span>
                        <span className={`fw-semibold ${getGradeColor(
                          getScorePercentage(user_submission.score, user_submission.max_score)
                        )}`}>
                          {user_submission.score}/{user_submission.max_score}
                        </span>
                      </div>
                    )}
                  </>
                )}

                <div className="mb-3">
                  <span className="text-muted small d-block">Course</span>
                  <span className="fw-semibold">{course?.title}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Resubmit Modal */}
        {showSubmitModal && (
          <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Resubmit Assignment</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowSubmitModal(false)}
                  />
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Upload File (Optional)</label>
                      <input
                        type="file"
                        className="form-control"
                        onChange={handleFileChange}
                        accept=".pdf,.doc,.docx,.zip,.rar"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Description (Optional)</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={submitForm.description}
                        onChange={(e) =>
                          setSubmitForm({ ...submitForm, description: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowSubmitModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={submitting}
                    >
                      {submitting ? "Submitting..." : "Resubmit"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AssignmentDetail;